import {course_class_getMyGradeResultByAuth_Api} from 'apis'
import {getId} from 'apis/model/base'
import _ from 'lodash'
import useAsync from 'modules/asyncCache/useAsync'
import useTranslate from 'modules/local/useTranslate'
import React, {useContext} from 'react'
import {BsCheckCircleFill, BsXCircleFill} from 'react-icons/bs'
import {IoInformationCircleOutline} from 'react-icons/io5'
import {PiExamFill} from 'react-icons/pi'
import classNames from 'classnames'
import {bindQueryParam} from 'views/AppLocals/functions/routerHelper'
import EmptyHolder from 'components/EmptyHolder'
import CourseClassContext from '../../CourseClassContext'
import {MyBoard} from './CourseClassGradeTab'

// Tab "Điểm số & Kết quả" (/classroom/:id) - self-service, CHỈ hiện với is_student=true
// (courseClassRouteEntities.js) - nội dung của tab này là ĐIỂM/KẾT QUẢ CỦA HỌC VIÊN (chính người
// xem, hoặc con em nếu xem với tư cách phụ huynh - resolve qua StudentUserLink ở BE, xem
// GradeController::getMyGradeResultByAuth), KHÔNG phải danh sách quản trị (đó là tab "Điểm số" khác,
// path 'grades', chỉ admin/GV thấy).
//
// Header tổng kết (điểm TB có trọng số/% chuyên cần/trạng thái đạt) lấy từ
// course_class_getMyGradeResultByAuth_Api - danh sách CHI TIẾT từng cột điểm bên dưới tái dùng
// NGUYÊN MyBoard (CourseClassGradeTab.js, vốn đã có sẵn cho case admin-kiêm-học-viên) thay vì dựng
// lại 1 danh sách song song.
const RESULT_STATUS_CONFIG = {
    1: {label: 'passed', className: 'text-green-700 bg-green-50 border-green-200', bar: 'bg-green-500'},
    0: {label: 'failed', className: 'text-primary-700 bg-primary-50 border-primary-200', bar: 'bg-primary-400'},
    '-1': {label: 'pending', className: 'text-color-400 background-100 border-color-100', bar: 'bg-color-200'},
}

// Dòng 1 điều kiện xét ĐẠT: icon ✓/✗ (hoặc chấm xám khi chưa cấu hình ngưỡng) + nhãn + giá trị
// thực đạt + ngưỡng yêu cầu.
const ConditionRow = ({pass, neutral = false, label, actual, required}) => (
    <div className="flex items-center gap-2 text-xs">
        {neutral
            ? <span style={{width: 13, height: 13}} className="flex-shrink-0 flex flex-center"><span style={{width: 6, height: 6}} className="rounded-full bg-color-200"/></span>
            : pass
                ? <BsCheckCircleFill size={13} className="text-green-500 flex-shrink-0"/>
                : <BsXCircleFill size={13} className="text-primary-400 flex-shrink-0"/>}
        <span className="text-color-300 flex-shrink-0">{label}</span>
        <span className={classNames('font-semibold', neutral ? 'text-color-100' : pass ? 'text-color-100' : 'text-primary-600')}>{actual}</span>
        {required != null && <span className="text-color-400">{`(${required})`}</span>}
    </div>
)

// Thẻ tổng kết 1 môn - hiện RÕ cách xét "Đạt": phải đủ CẢ 2 điều kiện (điểm ≥ điểm qua môn VÀ
// chuyên cần ≥ mức tối thiểu) - đúng logic StudentSubjectResult::computeAutoStatus() bên BE. Trước
// đây chỉ show 1 chữ "Đạt" + 1 chữ "Đủ điều kiện" rời rạc, GV/HV không hiểu đạt cái gì / tính sao.
const SubjectResultCard = ({item, showSubjectTitle}) => {
    const t = useTranslate()
    const {
        subject, score_number, passed_score, graded_count,
        attendance_percentage, passed_attendance_percentage,
        total_sessions, number_of_sessions,
        result_status,
    } = item ?? {}

    const hasScore = score_number !== null && score_number !== undefined
    const attendancePct = (attendance_percentage !== null && attendance_percentage !== undefined)
        ? attendance_percentage
        : (number_of_sessions > 0 ? Math.round((total_sessions / number_of_sessions) * 100) : null)
    const scoreWidth = hasScore ? Math.min(100, Math.max(0, Math.round((Number(score_number) / 10) * 100))) : 0

    // Tính CẢ 2 điều kiện + kết quả TRỰC TIẾP từ số hiện tại + ngưỡng vừa fetch - KHÔNG tin
    // eligibility_status/result_status đã lưu ở BE: 2 giá trị đó một chiều/không tự tính lại khi
    // admin đổi ngưỡng môn -> card từng hiện "Đạt" cạnh điều kiện chuyên cần rõ ràng trượt.
    const hasScoreReq = passed_score != null && passed_score > 0
    const hasAttReq = passed_attendance_percentage != null && passed_attendance_percentage > 0
    const scorePass = hasScore && (!hasScoreReq || Number(score_number) >= Number(passed_score))
    const attPass = !hasAttReq || (attendancePct != null && Number(attendancePct) >= Number(passed_attendance_percentage))
    // Khóa chưa đặt BẤT KỲ ngưỡng nào -> "Đạt" trơn gây hiểu nhầm (thực chất auto-pass). Trạng thái
    // trung tính riêng.
    const noCriteria = !hasScoreReq && !hasAttReq
    // -1 = chưa đủ dữ liệu (chưa có điểm), 1 = đạt, 0 = chưa đạt.
    const effResultStatus = noCriteria
        ? (result_status ?? -1)
        : !hasScore ? -1 : (scorePass && attPass ? 1 : 0)
    const isPending = effResultStatus === -1
    const resultCfg = RESULT_STATUS_CONFIG[String(effResultStatus)] ?? RESULT_STATUS_CONFIG['-1']

    return (
        <div className="flex flex-col rounded-2xl border border-color-100 background shadow-sm overflow-hidden">
            <div className={classNames('h-1 w-full', noCriteria ? 'bg-color-200' : resultCfg.bar)}/>
            <div className="p-4 flex flex-col gap-3">
                {showSubjectTitle && (
                    <span className="w-fit px-2 py-0.5 rounded-md background-100 font-semibold text-2xs text-color-300 uppercase tracking-wide">
                        {subject?.title}
                    </span>
                )}

                <div className="flex items-start justify-between gap-3">
                    <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-baseline gap-1">
                            {hasScore ? (
                                <>
                                    <span className="font-bold text-4xl text-color-000 leading-none">{score_number}</span>
                                    <span className="text-base text-color-300">/10</span>
                                </>
                            ) : (
                                <span className="font-semibold text-lg text-color-300">{t('not graded yet')}</span>
                            )}
                        </div>
                        <span className="text-xs text-color-400">
                            {graded_count > 1 ? t('average of {n} grade columns', {n: graded_count}) : t('current average score')}
                        </span>
                    </div>
                    <span className={classNames(
                        'flex-shrink-0 px-3 py-1 rounded-full border font-bold text-xs',
                        noCriteria ? 'text-color-400 background-100 border-color-100' : resultCfg.className
                    )}>
                        {noCriteria ? t('criteria not set') : `${t('result')}: ${t(resultCfg.label)}`}
                    </span>
                </div>

                {hasScore && hasScoreReq && (
                    <div className="h-1.5 rounded-full background-100 overflow-hidden">
                        <div
                            className={classNames('h-full rounded-full transition-all', scorePass ? 'bg-green-500' : 'bg-primary-400')}
                            style={{width: `${scoreWidth}%`}}
                        />
                    </div>
                )}

                {/* Điều kiện xét ĐẠT - cần đủ CẢ 2 */}
                <div className="pt-2 border-t border-color-50 flex flex-col gap-1.5">
                    <span className="text-2xs font-bold text-color-400 uppercase tracking-wide">
                        {t('passing conditions both required')}
                    </span>
                    <ConditionRow
                        pass={scorePass}
                        neutral={noCriteria}
                        label={t('score')}
                        actual={hasScore ? `${score_number}/10` : t('not graded yet')}
                        required={hasScoreReq ? `${t('passed score')}: ${passed_score}` : null}
                    />
                    <ConditionRow
                        pass={attPass}
                        neutral={noCriteria}
                        label={t('attendance rate')}
                        actual={`${number_of_sessions > 0 ? `${total_sessions}/${number_of_sessions}` : '—'}${attendancePct !== null ? ` · ${attendancePct}%` : ''}`}
                        required={hasAttReq ? `${t('min attendance')}: ${passed_attendance_percentage}%` : null}
                    />
                    <span className="mt-0.5 text-2xs text-color-400 italic leading-snug">
                        {noCriteria
                            ? t('no pass criteria set')
                            : isPending
                                ? t('awaiting more data to assess')
                                : t('pass rule hint')}
                    </span>
                </div>
            </div>
        </div>
    )
}

const CourseClassMyGradeResultTab = () => {
    const t = useTranslate()
    const {item} = useContext(CourseClassContext)
    const course_class_id = getId(item)

    const {response, isLoading} = useAsync({
        apiInfo: course_class_getMyGradeResultByAuth_Api,
        query: bindQueryParam({id: course_class_id}),
    })

    const data = _.get(response, 'data') ?? {}
    const student = data?.student
    const subjectResults = data?.subject_results ?? []

    if (isLoading) return null

    if (!student) {
        return <EmptyHolder title={t('no grade result available yet')}/>
    }

    return (
        <div className="flex flex-col gap-3">
            {/* Ghi chú phạm vi - nội dung tab này CHỈ dành cho học viên xem điểm/kết quả của chính
                mình (hoặc con em nếu là phụ huynh), KHÔNG phải màn quản trị điểm (bạn yêu cầu). CÙNG
                style banner hướng dẫn đang dùng ở tab "Lịch học & Điểm danh"
                (CourseClassTimetableTab.js: icon + nền indigo-50 + viền indigo-100), thay bản cũ chỉ
                chữ nghiêng đơn giản - cho đồng bộ giữa 2 sub-tab cùng nằm trong "Lịch trình & Tiến độ"
                (bạn báo 2 tab liền nhau quá, cần tạo phân cách rõ hơn). */}
            <div className="px-3 py-2 flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                <IoInformationCircleOutline size={15} className="text-indigo-500 flex-shrink-0"/>
                <span className="text-xs text-indigo-700 leading-snug">
                    {t('grades and results tab guide description')}
                </span>
            </div>

            <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl">
                <PiExamFill size={16} className="text-blue-500 flex-shrink-0"/>
                <span className="text-xs text-blue-600">
                    {t('viewing grade result of')}
                    {': '}
                    <span className="font-semibold">{student.fullname}</span>
                    {student.student_idcode && (
                        <span className="ml-1 text-blue-400">({student.student_idcode})</span>
                    )}
                </span>
            </div>

            <div className="flex flex-col gap-3">
                {subjectResults.map((sr, i) => (
                    <SubjectResultCard key={sr?.subject?.id ?? i} item={sr} showSubjectTitle={subjectResults.length > 1}/>
                ))}
            </div>

            <div className="mt-1">
                <span className="font-semibold text-sm text-gray-600 tracking-wide">
                    {t('list of grades')}
                </span>
                <div className="mt-2">
                    <MyBoard course_class_id={course_class_id}/>
                </div>
            </div>
        </div>
    )
}

export default CourseClassMyGradeResultTab
